// File: src/components/EventsFiltersAndHeadline.jsx
import React from 'react';

import DateFilterComponent from './DateFilterComponent';
import SortBy from './SortBy';

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function getThisWeekendRange() {
  const today = new Date();
  const day = today.getDay();

  let saturday;

  if (day === 6) {
    saturday = today;
  } else {
    const daysUntilSaturday = (6 - day + 7) % 7;
    saturday = addDays(today, daysUntilSaturday);
  }

  const sunday = addDays(saturday, 1);

  return {
    startDate: startOfDay(saturday),
    endDate: endOfDay(sunday),
  };
}

function getQuickRange(type) {
  const today = new Date();

  if (type === 'today') {
    return {
      startDate: startOfDay(today),
      endDate: endOfDay(today),
    };
  }

  if (type === 'weekend') {
    return getThisWeekendRange();
  }

  if (type === 'next7') {
    return {
      startDate: startOfDay(today),
      endDate: endOfDay(addDays(today, 7)),
    };
  }

  if (type === 'month') {
    return {
      startDate: startOfDay(today),
      endDate: endOfDay(addDays(today, 30)),
    };
  }

  return {
    startDate: null,
    endDate: null,
  };
}

function sameDay(a, b) {
  if (!a || !b) return false;

  const da = new Date(a);
  const db = new Date(b);

  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

function getActiveQuickFilter(range) {
  const item = Array.isArray(range) && range.length ? range[0] : null;
  if (!item?.startDate || !item?.endDate) return null;

  const todayRange = getQuickRange('today');
  const weekendRange = getQuickRange('weekend');
  const next7Range = getQuickRange('next7');
  const monthRange = getQuickRange('month');

  if (
    sameDay(item.startDate, todayRange.startDate) &&
    sameDay(item.endDate, todayRange.endDate)
  ) {
    return 'today';
  }

  if (
    sameDay(item.startDate, weekendRange.startDate) &&
    sameDay(item.endDate, weekendRange.endDate)
  ) {
    return 'weekend';
  }

  if (
    sameDay(item.startDate, next7Range.startDate) &&
    sameDay(item.endDate, next7Range.endDate)
  ) {
    return 'next7';
  }

  if (
    sameDay(item.startDate, monthRange.startDate) &&
    sameDay(item.endDate, monthRange.endDate)
  ) {
    return 'month';
  }

  return null;
}

export default function EventsFiltersAndHeadline({
  userId,
  filterType,
  setFilterType,
  selectedDateRange,
  setSelectedDateRange,
  sortType,
  setSortType,
  sortOpen,
  setSortOpen,
  dateOpen,
  setDateOpen,
  filtersActive = false,
}) {
  const safeRange = (Array.isArray(selectedDateRange) && selectedDateRange.length
    ? selectedDateRange
    : [{ startDate: null, endDate: null, key: 'selection', times: [] }]
  ).map((item) => ({
    ...item,
    times: Array.isArray(item.times) ? item.times : [],
  }));

  const activeQuickFilter = getActiveQuickFilter(safeRange);

  const clearDateFilters = () => {
    setSelectedDateRange([
      {
        startDate: null,
        endDate: null,
        key: 'selection',
        times: [],
      },
    ]);

    setDateOpen(false);
  };

  const handleQuickFilter = (type) => {
    const currentlyActive = activeQuickFilter === type;

    if (currentlyActive) {
      clearDateFilters();
      return;
    }

    const nextRange = getQuickRange(type);

    setSelectedDateRange([
      {
        startDate: nextRange.startDate,
        endDate: nextRange.endDate,
        key: 'selection',
        times: safeRange[0]?.times || [],
      },
    ]);

    setDateOpen(false);
  };

  const quickFilters = [
    { id: 'today', label: 'Today' },
    { id: 'weekend', label: 'This weekend' },
    { id: 'next7', label: 'Next 7 days' },
    { id: 'month', label: 'This month' },
  ];

  return (
    <>
      <div
        className={`eventsFiltersHeadlineContainer${
          filtersActive ? ' filtersActive' : ''
        }`}
        style={{
          display: 'flex',
          alignItems: 'center',
          paddingLeft: 4,
        }}
      >
        <div className="discoverHeadline" style={headlineWrapStyle}>
          <h2
            className="eventsHeadlineTitle"
            style={{
              ...titleStyle,
              marginTop: 10,
            }}
          >
            Choose your next gathering
          </h2>

          {/*
          <div style={quickFilterRowStyle}>
            {quickFilters.map((filter) => {
              const active = activeQuickFilter === filter.id;

              return (
                <button
                  key={filter.id}
                  type="button"
                  style={{
                    ...quickFilterPillStyle,
                    ...(active ? quickFilterPillActiveStyle : {}),
                  }}
                  onClick={() => handleQuickFilter(filter.id)}
                >
                  {filter.label}
                </button>
              );
            })}
          </div>
          */}
        </div>

        <div className="dateFilterWrapper" style={{ marginLeft: 20 }}>
          <DateFilterComponent
            range={safeRange}
            onChange={setSelectedDateRange}
            dateOpen={dateOpen}
            setDateOpen={setDateOpen}
          />
        </div>

        <div className="sortByWrapper" style={{ marginLeft: 'auto' }}>
          <SortBy
            sortType={sortType}
            setSortType={setSortType}
            sortOpen={sortOpen}
            setSortOpen={setSortOpen}
          />
        </div>
      </div>

      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Merriweather+Sans:wght@800&display=swap');

          .eventsHeadlineTitle {
            background-color: #111;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='96' height='96' viewBox='0 0 96 96'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.72' numOctaves='3' stitchTiles='stitch'/%3E%3CfeComponentTransfer%3E%3CfeFuncR type='linear' slope='.45' intercept='.55'/%3E%3CfeFuncG type='linear' slope='.45' intercept='.55'/%3E%3CfeFuncB type='linear' slope='.45' intercept='.55'/%3E%3CfeFuncA type='table' tableValues='0 .18'/%3E%3C/feComponentTransfer%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='.34'/%3E%3C/svg%3E");
            background-repeat: repeat;
            background-size: 96px 96px;
            background-blend-mode: screen;
            -webkit-background-clip: text;
            background-clip: text;
            -webkit-text-fill-color: transparent;
            color: transparent;
          }

          @media (max-width: 768px) {
            .eventsFiltersHeadlineContainer {
              display: flex;
              flex-wrap: wrap;
              align-items: center !important;
              padding-left: 4px !important;
              margin-top: 8px;
              margin-bottom: 8px;
            }

            .discoverHeadline {
              order: 0;
              width: 100%;
              max-width: 100%;
              margin-right: 0;
              padding-right: 0;
              margin-bottom: 12px;
            }

            .eventsFiltersHeadlineContainer.filtersActive {
              margin-bottom: 0;
            }

            .eventsFiltersHeadlineContainer.filtersActive
              .discoverHeadline {
              margin-bottom: 4px;
            }

            .sortByWrapper {
              order: 1;
              margin-left: auto !important;
            }

            .dateFilterWrapper {
              order: 2;
              margin-left: -500px !important;
            }
          }
        `}
      </style>
    </>
  );
}

const headlineWrapStyle = {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  padding: '0 0 0px',
};

const titleStyle = {
  margin: 0,
  fontFamily: "'Merriweather Sans', sans-serif",
  fontSize: '18px',
  lineHeight: 1,
  fontWeight: 800,
  letterSpacing: '-0.0px',
  textTransform: 'uppercase',
};

const quickFilterRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 12,
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'none',
};

const quickFilterPillStyle = {
  border: '1px solid rgba(17,17,17,0.1)',
  background: '#F7F7F8',
  color: 'rgba(17,17,17,0.72)',
  borderRadius: 999,
  padding: '8px 12px',
  fontSize: '.82rem',
  lineHeight: 1,
  fontWeight: 650,
  letterSpacing: '-0.02em',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
};

const quickFilterPillActiveStyle = {
  background: '#EEF0F3',
  color: '#050505',
  border: '1px solid #D8DCE2',
};

const quickFilterClearStyle = {
  background: '#F7F7F7',
  color: '#111',
  borderStyle: 'dashed',
  borderColor: '#E0E0E0',
  fontWeight: 800,
};
